import {useMemo} from 'react';
import {Link as RoutedLink} from 'react-router-dom';

import {Stack} from 'shells/layout/stack/stack';
import {Link} from 'shells/link/link';
import {Table} from 'shells/table/table';
import {Body} from 'shells/typography/body';

import type {GetChatbotReport_chatbotReport_aiMetrics_knowledgeGaps as KnowledgeGap} from 'nutshell-graphql-types';

import {analyticsClient} from '../../../analytics-client';
import {appHistory} from '../../../history';

import {useGetSessionUser} from '../../core/use-get-session-user';

import {ChatbotReportTableEmptyState} from './chatbot-report-table-empty-state';
import {THREAD_SIDEBAR_OPEN_SOURCES} from './consts';
import {useOpenThreadListSidebar} from './use-open-thread-list-sidebar';

interface Props {
    knowledgeGaps: KnowledgeGap[];
}

export function KnowledgeGapsSection(props: Props) {
    const openThreadListSidebar = useOpenThreadListSidebar();
    const {user} = useGetSessionUser();
    const userCanAccessSetup = Boolean(user && user.permissions.canAccessSetup);

    const handleOpenThreads = (row: {original: KnowledgeGap}) => {
        openThreadListSidebar({
            threadIds: row.original.threadIds,
            title: row.original.question,
            subtitle: 'View recent chats on similar inquiries',
            source: THREAD_SIDEBAR_OPEN_SOURCES.KNOWLEDGE_GAPS_SECTION,
        });
    };

    const columns = useMemo(
        () => [
            {
                id: 'question',
                Header: 'Questions',
                accessor: (row: KnowledgeGap) => row,
                dataType: 'component',
                Cell: ({cell}: {cell: {value: KnowledgeGap}}) => (
                    <div className='py-8'>
                        <Body size='small' color='dark'>
                            {cell.value.question}
                        </Body>
                    </div>
                ),
            },
            {
                id: 'threads',
                Header: 'Threads',
                accessor: (row: KnowledgeGap) => row.threadCount,
                dataType: 'integer',
                width: 100,
            },
        ],
        []
    );

    const specialStates = useMemo(
        () => [
            {
                shouldRender: () => props.knowledgeGaps.length === 0,
                component: (
                    <ChatbotReportTableEmptyState text='Great news! All conversations with your chatbot were successfully resolved' />
                ),
            },
        ],
        [props.knowledgeGaps]
    );

    const knowledgeGapsDescription = userCanAccessSetup ? (
        <>
            Questions chatbot could not provide the visitor a definitive answer due to lack of
            information. Add more documentation in{' '}
            <Link
                size='small'
                variant='primary'
                // @ts-expect-error
                as={RoutedLink}
                to='/inbox/settings/chat/knowledge'
                onClick={(e: React.MouseEvent) => {
                    e.preventDefault();
                    analyticsClient
                        .track(analyticsClient.EVENTS.TRACK_KNOWLEDGE_GAPS_LINK_CLICKED_IN_CR)
                        .then(() => {
                            appHistory.push('/inbox/settings/chat/knowledge');
                        });
                }}
            >
                chatbot knowledge settings
            </Link>{' '}
            to improve these experiences.
        </>
    ) : (
        <>
            Questions chatbot could not provide the visitor a definitive answer due to lack of
            information. Add more documentation to improve these experiences.
        </>
    );

    return (
        <Stack spacing={16}>
            <Stack spacing={4}>
                <Body isBold={true} color='dark'>
                    Knowledge gaps
                </Body>
                <Body size='small'>{knowledgeGapsDescription}</Body>
            </Stack>
            <Table
                // @ts-expect-error
                columns={columns}
                preserveHeaderInSpecialState={true}
                specialStates={specialStates}
                disableSorting={true}
                data={props.knowledgeGaps}
                hasHeaderBorder={true}
                isCompact={true}
                hasBorders={true}
                disableFilters={true}
                onSelectRow={handleOpenThreads}
                headerBackgroundColor='offWhiteDk'
                fullWidth={true}
            />
        </Stack>
    );
}
